/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { createContext, type ReactElement, useContext, useEffect, useRef, useState } from "react";

import { useUserContext } from './context'
import { type WebsocketMessage } from '../api/types'
import LogTool from '../logger/logTools'
import { fetchCurrentUser } from '../api/userApi'
import { refreshAccessToken } from '../api/authTokens'
/* ------------------------- Functional imports end ------------------------- */

interface IWebsocketContext {
  websocketMessage: WebsocketMessage | null
  websocketIsReady: boolean
  websocketSend: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function WebsocketProvider(props: { children: React.ReactNode }): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'WebsocketProvider', enable: true, logLevel: 'debug' })
  const { user, accessToken } = useUserContext()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [userIsAuthenticated, setAuthenticated] = useState(Boolean(Cookies.get('access')))
  const [isReady, setIsReady] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const ws = useRef<WebSocket | undefined>(undefined)
  const [message, setMessage] = useState<WebsocketMessage | null>(null)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    // try to connect the websocket the first time we know about an authenticated user
    if (user && accessToken && !isReady) {
      log.info('Connecting websocket. User is authenticated:', userIsAuthenticated)
      connectWebsocket().catch((error) => {
        log.error('Failed to connect websocket:', error)
      })
      
      return () => {
        if (ws.current) {
          ws.current.close()
        }
      }
    }
  }, [user])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Check if a JWT token is expired or about to expire (within 60 seconds)
   * @param token The JWT token to check
   * @returns true if token is expired or about to expire
   */
  function isTokenExpiredOrExpiringSoon(token: string): boolean {
    try {
      const decoded = jwtDecode<{ exp: number }>(token)
      const expirationTime = decoded.exp * 1000 // Convert to milliseconds
      const currentTime = Date.now()
      const bufferTime = 60 * 1000 // 60 seconds buffer
      return expirationTime <= (currentTime + bufferTime)
    } catch (error) {
      log.warn('Failed to decode token, assuming expired:', error)
      return true
    }
  }

  /**
   * Get a valid access token, refreshing if necessary
   * @returns A valid access token or undefined if refresh fails
   */
  async function getValidAccessToken(): Promise<string | undefined> {
    let accessToken = Cookies.get('access')
    
    if (!accessToken) {
      log.info('No access token found, attempting to refresh')
      accessToken = await refreshAccessToken()
      return accessToken ?? undefined
    }

    // Check if token is expired or about to expire
    if (isTokenExpiredOrExpiringSoon(accessToken)) {
      log.info('Access token expired or expiring soon, refreshing...')
      accessToken = await refreshAccessToken()
      return accessToken ?? undefined
    }

    return accessToken
  }

  /**
   * Create a new WebSocket instance that is configured to load new messages into
   * the message state and reconnect automatically if the connection is interrupted.
   * @returns Promise<WebSocket>
   */
  async function connectWebsocket(): Promise<WebSocket> {
    // generate a new websocket url every time a new socket is created to ensure that we always use the newest access token
    const wsUrlEnv: string | undefined = process.env.REACT_APP_WS_URL
    if (!wsUrlEnv) {
      throw Error("Websocket url is not defined. Please add the the URL via the REACT_APP_WS_URL environment variable")
    }
    
    // Get a valid access token (refresh if needed)
    const accessToken = await getValidAccessToken()
    if (!accessToken) {
      throw Error("Access token is not available. Cannot connect to WebSocket without authentication.")
    }
    
    // Ensure proper URL formatting (handle trailing slash)
    const baseUrl = wsUrlEnv.endsWith('/') ? wsUrlEnv : `${wsUrlEnv}/`
    const wsUrl = `${baseUrl}authenticated_socket/?token=${accessToken}`
    const socket = new WebSocket(wsUrl)

    socket.onopen = () => {
      log.info('Websocket connected. Ready to receive and send messages!')
      setIsReady(true)
    }
    socket.onclose = async (ev: CloseEvent): Promise<void> => {
      if (ev.wasClean) {
        log.info('Websocket connection closed by the server. Code ->', ev.code)
      } else {
        log.warn('Websocket disconnected. Unable to receive or send messages until reconnected!')

        // try to reconnect after a few seconds
        // making sure the access token is valid when we are trying to reconnect
        try {
          // Refresh token before reconnecting
          const refreshedToken = await refreshAccessToken()
          if (!refreshedToken) {
            log.error('Failed to refresh token, cannot reconnect websocket')
            await authenticateUser()
          }
          
          setTimeout(() => {
            log.info('Trying to reconnect websocket')
            void connectWebsocket().catch((error) => {
              log.error('Failed to reconnect websocket:', error)
            })
          }, 5 * 1000)
        } catch (error) {
          log.error('Error during websocket reconnection attempt:', error)
        }
      }
      setIsReady(false)
    }
    
    socket.onerror = (ev: Event) => {
      log.error('Websocket error occurred:', ev)
    }
    socket.onmessage = (ev: MessageEvent<unknown>) => {
      if (typeof ev.data === "string") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const messageObj: WebsocketMessage = JSON.parse(ev.data);
        log.info('Received message ->', messageObj)
        setMessage(messageObj)
      }
    }
    ws.current = socket
    return socket
  }

  /**
   * Authenticated the user / refresh the access token by fetching accounts/users/self/.
   */
  async function authenticateUser(): Promise<void> {
    log.info('Begin fetching user/self to make sure that the user is authenticated')
    try {
      await fetchCurrentUser()
      log.info('Success authenticating user')
      setAuthenticated(true)
    } catch (error) {
      log.error('Error authenticating user', error)

      // if the authentication failed, the websocket will not be connected.
      // Try again to authenticate the user to enable websocket connection!
      // setTimeout(() => {
      //   log.info('Trying to authenticate user one more time')
      //   authenticateUser()
      // }, 10 * 1000)
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const providerValue: IWebsocketContext = {
    websocketMessage: message,
    websocketIsReady: isReady,
    websocketSend: ws.current?.send
      // eslint-disable-next-line @typescript-eslint/unbound-method
      ? ws.current.send
      : () => {
        throw new Error(
          'Websocket is not ready to send messages yet. Wait until websocketIsReady is true!'
        )
      },
  }
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <WebsocketContext.Provider value={providerValue}>{props.children}</WebsocketContext.Provider>
  )
}

const WebsocketContext = createContext<IWebsocketContext>({
  websocketMessage: null,
  websocketIsReady: false,
  websocketSend: () => {
    throw new Error('Websocket is not ready to send messages yet. Wait until isReady is true!')
  },
})

export const useWebsocket = (): IWebsocketContext => useContext(WebsocketContext)
